import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import "../style/LeadSection.css";
import { Briefcase, Camera, CheckCircle, MessageSquare, Receipt, ShieldCheck } from "lucide-react";

const LeadSection = () => {
    const scrollContainerRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    const features = [
        {
            icon: <Briefcase style={{ color: "#2563EB", width: 'fit' }} />,
            title: "My Jobs (Employees)",
            description: "Employees see only the jobs assigned to them — clear, simple, and without exposing your full customer list.",
            color: "#2563EB"
        },
        {
            icon: <CheckCircle style={{ color: "#FF8C00", width: 'fit' }} />,
            title: "Job Completion",
            description: "Team members can mark jobs as complete with one tap, keeping you updated without constant messaging.",
            color: "#FF8C00"
        },
        {
            icon: <Camera style={{ color: "#1E40AF", width: 'fit' }} />,
            title: "Photos & Evidence",
            description: "Upload before, during, and after photos to keep a permanent record of every job.",
            color: "#1E40AF"
        },
        {
            icon: <Receipt style={{ color: "#10B981", width: 'fit' }} />,
            title: "Materials & Receipts",
            description: "Attach receipts for materials purchased for each job, helping track costs and share proof with customers.",
            color: "#10B981"
        },
        {
            icon: <ShieldCheck style={{ color: "#EC4899", width: 'fit' }} />,
            title: "Staff Permissions",
            description: "Control what employees can access, protecting sensitive customer and financial information.",
            color: "#EC4899"
        },
        {
            icon: <MessageSquare style={{ color: "#06B6D4", width: 'fit' }} />,
            title: "Customer Messaging (SMS & Email)",
            description: "Send job confirmations, quotes, invoices, and reminders via SMS, Email, or both — automatically.",
            color: "#06B6D4"
        }
    ];

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const container = scrollContainerRef.current;
            if (!container) return;

            const scrollLeft = container.scrollLeft;
            const cardWidth = container.querySelector('.feature-card-lead')?.offsetWidth || 0;
            const gap = isMobile ? 20 : 30;
            
            // Calculate which card is most visible
            const index = Math.round(scrollLeft / (cardWidth + gap));
            setActiveIndex(Math.min(Math.max(0, index), features.length - 1));
        };

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll(); // Initial call
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [features.length, isMobile]);

    const scrollToCard = (index) => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const cardWidth = container.querySelector('.feature-card-lead')?.offsetWidth || 0;
        const gap = isMobile ? 20 : 30;
        const scrollPosition = index * (cardWidth + gap);

        container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
        });
    };

    const handlePrevious = () => {
        if (activeIndex > 0) {
            scrollToCard(activeIndex - 1);
        }
    };

    const handleNext = () => {
        if (activeIndex < features.length - 1) {
            scrollToCard(activeIndex + 1);
        }
    };

    const cardVariants = {
        hidden: { opacity: 0 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.1,
                duration: 0.5
            }
        })
    };

    return (
        <section className="lead-section">
            <div className="container">
                {/* Section Header */}
                <motion.div
                    className="lead-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="section-title-lead">Team Work & Job Completion</h2>
                    <p className="section-subtitle-lead">
                        Everything your team needs to complete work efficiently and keep you updated.
                    </p>
                </motion.div>

                {/* Horizontal Scrollable Cards */}
                <div className="scroll-wrapper-lead">
                    {/* Previous Button - Desktop Only */}
                    {!isMobile && (
                        <button
                            className={`nav-button-lead nav-button-left-lead ${activeIndex === 0 ? 'disabled' : ''}`}
                            onClick={handlePrevious}
                            disabled={activeIndex === 0}
                            aria-label="Previous card"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    )}

                    <motion.div
                        className="features-scroll-lead"
                        ref={scrollContainerRef}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                    >
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="feature-card-lead"
                                custom={index}
                                variants={cardVariants}
                                whileHover={{
                                    y: -10,
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div
                                    className="feature-icon-wrapper-lead"
                                    style={{ backgroundColor: `${feature.color}20` }}
                                >
                                    <span className="feature-icon-lead">
                                        {feature.icon}
                                    </span>
                                </div>
                                <h3 className="feature-title-lead">{feature.title}</h3>
                                <p className="feature-description-lead">{feature.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Next Button - Desktop Only */}
                    {!isMobile && (
                        <button
                            className={`nav-button-lead nav-button-right-lead ${activeIndex === features.length - 1 ? 'disabled' : ''}`}
                            onClick={handleNext}
                            disabled={activeIndex === features.length - 1}
                            aria-label="Next card"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    )}

                    {/* Scroll Indicators (Bubbles) */}
                    <div className="scroll-indicators">
                        {features.map((_, index) => (
                            <button
                                key={index}
                                className={`scroll-bubble ${activeIndex === index ? 'active' : ''}`}
                                onClick={() => scrollToCard(index)}
                                aria-label={`Scroll to card ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LeadSection;